import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiCalendarToday,
  mdiMapMarkerOutline,
  mdiNavigation,
  mdiTextBoxOutline,
} from "@mdi/js";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import {
  updateJobSetup,
  selectCalendarLoading,
  clearJobSetupError,
  selectSectionTemplates,
} from "app/containers/JobCalendar/slice";
import { navigateToAppointment } from "app/containers/JobCalendar/utils/mapUtils";
import { formatAddress } from "app/containers/Jobs/utils/format";
import {
  computeContractTrack,
  computePaymentTrack,
} from "app/containers/JobCalendar/utils/sendTrackRules";
import { computeFieldLocks } from "app/containers/JobCalendar/utils/fieldLockRules";
import { buildSendMessages } from "app/containers/JobCalendar/utils/sendFeedback";
import { buildJobContractData } from "app/containers/JobCalendar/utils/contractPreviewData";
import CustomToast from "app/components/common/CustomToast";
import ModalHeader from "app/components/common/ModalHeader";
import { useApp } from "app/contexts/AppContext";
import InfoIconSlot from "./InfoIconSlot";
import PropertyDetails, { toPropertyDetails } from "./PropertyDetails";
import JobSetupSendTracks from "./JobSetupSendTracks";
import ContractPreviewLightbox from "./ContractPreviewLightbox";
import LockHintDialog from "./LockHintDialog";
import LockedField, { LockNotice } from "./LockedField";
import "./JobSetupModal.css";

const JobSetupModal = ({
  show,
  onHide,
  appointment,
  // Save & Send props (all optional; callers spread them from useJobSetupSend):
  contractState = null, // { status }
  paymentState = null, // { status }
  tier = null, // "Free" | "Solo"
  stripeConnected = false,
  stripeLoading = false,
  contractTemplates = [],
  onRequestUpgrade,
  onSendTracks, // async ({ sendContract, sendPayment, quote }) => { contract, payment }
  // Fires only after a successful save. onHide cannot stand in for it: it also
  // runs on Cancel and Esc, so a caller that refetches there refetches for
  // nothing. Same split as AppointmentModal.
  onSaved,
  onNavigateToDetail,
  // The calendar fetches contract / payment status itself; when that fails the
  // send tracks are unknown rather than "nothing to send".
  trackStatesFailed = false,
  onRetryTrackStates,
  // No connected mailbox blocks the contract (its email is the only signing
  // invite). Defaults keep callers that never checked it unblocked.
  contractEmailConnected = true,
  contractEmailLoading = false,
  // Why a connected inspector's contract email still would not go out; shown,
  // never blocking (AS-917 Q3).
  contractEmailWarning = null,
}) => {
  const dispatch = useDispatch();
  const { device } = useApp();
  const loading = useSelector(selectCalendarLoading);
  const sectionTemplates = useSelector(selectSectionTemplates);

  const [formData, setFormData] = useState({
    template: null,
    quote: "",
    note: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // The job's own template must always be offered, even when the fetched list
  // does not contain it. A <select> whose value matches no option renders
  // option[0] instead, so the modal would show — and on Save write — a
  // template the job never had.
  const templateOptions = useMemo(() => {
    const current = formData.template;
    if (!current?.id) return sectionTemplates;
    return sectionTemplates.some((t) => t.id === current.id)
      ? sectionTemplates
      : [...sectionTemplates, current];
  }, [sectionTemplates, formData.template]);

  // A stack: sending both tracks reports each one separately, so a partial
  // failure names the side that failed instead of collapsing into one line.
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  // Save & Send state
  const [contractChecked, setContractChecked] = useState(false);
  const [paymentChecked, setPaymentChecked] = useState(false);
  const [contractTouched, setContractTouched] = useState(false);
  const [paymentTouched, setPaymentTouched] = useState(false);
  const [selectedContractTemplateId, setSelectedContractTemplateId] =
    useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [lockDialog, setLockDialog] = useState({
    show: false,
    variant: "contract",
  });

  const pushToast = (message, variant = "success") =>
    setToasts((prev) => [
      ...prev,
      { id: (toastIdRef.current += 1), message, variant },
    ]);
  const dismissToast = (id) =>
    setToasts((prev) => prev.filter((toast) => toast.id !== id));

  // Quote carries a stacked lock (contract + payment both reference the amount);
  // the contract template locks once the contract is out.
  const locks = computeFieldLocks({
    contractStatus: contractState?.status,
    paymentStatus: paymentState?.status,
  });
  const sectionTemplateLocked = appointment?.job?.report?.item_count > 0;
  const contractSigned = contractState?.status === "signed";
  const openLockDialog = (variant) => setLockDialog({ show: true, variant });

  const isEditMode = appointment?.job?.note || appointment?.job?.quote;

  const defaultContractTemplateId =
    (contractTemplates.find((tpl) => tpl.is_default) || contractTemplates[0])
      ?.id || "";

  // Compute the current gating for each send track (pure, cheap).
  const contractTrack = contractState
    ? computeContractTrack({
        status: contractState.status,
        tier,
        emailConnected: contractEmailConnected,
        emailLoading: contractEmailLoading,
        emailWarning: contractEmailWarning,
        appointment,
        quote: formData.quote,
      })
    : { show: false };
  const paymentTrack = paymentState
    ? computePaymentTrack({
        status: paymentState.status,
        tier,
        stripeConnected,
        stripeLoading,
        appointment,
        quote: formData.quote,
      })
    : { show: false };

  // Initialize form + send state whenever the modal opens.
  useEffect(() => {
    if (show && appointment) {
      dispatch(clearJobSetupError());
      setToasts([]);

      const jobData = appointment.job || {};
      let selectedTemplate = null;
      const templateId = jobData.template?.id;
      if (templateId) {
        // Never fall back to null when the job HAS a template. An id that is
        // not in the list (list still loading, template deleted, a paging bug)
        // would leave formData.template null, and Save would then write
        // template_id: null and wipe the job's section template. Keeping the
        // id round-trips it untouched instead.
        selectedTemplate = sectionTemplates.find(
          (template) => template.id === templateId
        ) || {
          id: templateId,
          name: jobData.template?.name || "",
        };
      } else {
        selectedTemplate =
          sectionTemplates.find((template) => template.isSystemDefault) || null;
      }

      setFormData({
        template: selectedTemplate,
        quote: jobData.quote ?? "",
        note: jobData.note || "",
      });
      setFormErrors({});

      setContractTouched(false);
      setPaymentTouched(false);
      setSelectedContractTemplateId(defaultContractTemplateId);
      setShowPreview(false);
      setLockDialog({ show: false, variant: "contract" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Pre-select the inspector's default contract once templates arrive. The
  // calendar fetches them when Setup opens, so they can land after the init
  // effect above has already run with an empty list.
  useEffect(() => {
    if (show && !selectedContractTemplateId && defaultContractTemplateId) {
      setSelectedContractTemplateId(defaultContractTemplateId);
    }
  }, [show, selectedContractTemplateId, defaultContractTemplateId]);

  // Keep the contract checkbox consistent as requirements change (unless the
  // user has manually toggled it).
  useEffect(() => {
    if (!show) return;
    if (!contractTrack.show || contractTrack.disabled) {
      setContractChecked(false);
    } else if (!contractTouched) {
      setContractChecked(!!contractTrack.autoCheck);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    contractTrack.show,
    contractTrack.disabled,
    contractTrack.autoCheck,
    contractTouched,
  ]);

  useEffect(() => {
    if (!show) return;
    if (!paymentTrack.show || paymentTrack.disabled) {
      setPaymentChecked(false);
    } else if (!paymentTouched) {
      setPaymentChecked(!!paymentTrack.autoCheck);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    paymentTrack.show,
    paymentTrack.disabled,
    paymentTrack.autoCheck,
    paymentTouched,
  ]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (
      formData.quote &&
      (isNaN(formData.quote) || parseFloat(formData.quote) < 0)
    ) {
      errors.quote = "Please enter a valid quote amount";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const wantContract = contractTrack.show && contractChecked;
  const wantPayment = paymentTrack.show && paymentChecked;
  const isSendMode = wantContract || wantPayment;

  const showStripeHint = () =>
    pushToast("Connect Stripe to send payment requests.", "default");

  const handleToggleContract = (next) => {
    if (contractTrack.mode === "upgrade") {
      onRequestUpgrade?.();
      return;
    }
    setContractTouched(true);
    setContractChecked(next);
  };

  const handleTogglePayment = (next) => {
    if (paymentTrack.mode === "upgrade") {
      onRequestUpgrade?.();
      return;
    }
    if (paymentTrack.mode === "stripe-hint") {
      showStripeHint();
      return;
    }
    setPaymentTouched(true);
    setPaymentChecked(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const jobId = appointment.job?.id;
      if (!jobId) throw new Error("Job ID not found in appointment");

      const jobSetupData = {
        note: formData.note || "",
        quote: formData.quote ? formData.quote : null,
        template_id: formData.template ? formData.template.id : null,
      };

      await dispatch(updateJobSetup({ jobId, jobSetupData })).unwrap();

      if (isSendMode && onSendTracks) {
        const result = await onSendTracks({
          sendContract: wantContract,
          sendPayment: wantPayment,
          quote: formData.quote,
          contractTemplateId: selectedContractTemplateId,
        });
        buildSendMessages({ result, wantContract, wantPayment }).forEach(
          ({ message, variant }) => pushToast(message, variant)
        );
      } else {
        pushToast(
          isEditMode
            ? "Job Setup updated successfully."
            : "Job Setup created successfully."
        );
      }

      onSaved?.();
      setTimeout(() => onHide(), 1500);
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? "update" : "create"} job setup:`,
        error
      );
      pushToast(
        `Failed to ${isEditMode ? "update" : "create"} Job Setup. Please try again.`,
        "error"
      );
    }
  };

  const handleClose = () => {
    setFormErrors({});
    onHide();
  };

  const getAppointmentDisplayData = () => {
    if (!appointment) return null;

    const startTime = moment(
      (appointment.start_time || appointment.start).substring(0, 19)
    );
    const endTime = moment(
      (appointment.end_time || appointment.end).substring(0, 19)
    );
    const dateTimeDisplay = `${startTime.format("MMM D, h:mm A")} - ${endTime.format("h:mm A")}`;

    const addressDisplay = formatAddress(appointment.job?.report?.address);

    const propertyDetails = toPropertyDetails(
      appointment.job?.report,
      appointment
    );

    return { dateTimeDisplay, addressDisplay, propertyDetails };
  };

  // Stable per appointment / quote, so the preview lightbox is not handed a
  // fresh object (and re-sanitized) on every keystroke elsewhere in the form.
  const contractPreviewData = useMemo(
    () => buildJobContractData({ appointment, quote: formData.quote }),
    [appointment, formData.quote]
  );

  const displayData = getAppointmentDisplayData();
  if (!displayData) return null;

  const selectedContractTemplate =
    contractTemplates.find((tpl) => tpl.id === selectedContractTemplateId) ||
    null;

  const primaryLabel = loading.jobSetup
    ? "Saving..."
    : isSendMode
      ? "Save & Send"
      : "Save";

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      className="job-setup-modal"
      backdrop="static"
    >
      <div className="job-setup-form">
        <ModalHeader title={isEditMode ? "Edit Job Setup" : "Add Job Setup"} />

        <div className="job-setup-content">
          <Form onSubmit={handleSubmit}>
            <div className="job-info-section">
              <div className="appointment-info">
                <div className="datetime-display">
                  <Icon
                    path={mdiCalendarToday}
                    size="24px"
                    className="datetime-icon"
                  />
                  <span className="datetime-text">
                    {displayData.dateTimeDisplay}
                  </span>
                  {appointment.note && (
                    <InfoIconSlot
                      text={appointment.note}
                      iconClassName="info-icon"
                    />
                  )}
                </div>
              </div>

              <div className="site-info">
                <div className="address-section">
                  <Icon
                    path={mdiMapMarkerOutline}
                    size="24px"
                    className="location-icon"
                  />
                  <span className="address-text">
                    {displayData.addressDisplay}
                  </span>
                  <Button
                    variant="link"
                    className="navigation-button"
                    onClick={() => navigateToAppointment(appointment)}
                    aria-label="Navigate to address"
                  >
                    <Icon path={mdiNavigation} size="20px" />
                  </Button>
                </div>

                <div className="job-setup-property-indent">
                  <PropertyDetails details={displayData.propertyDetails} />
                </div>
              </div>
            </div>

            <hr className="section-divider" />

            <div className="job-setup-section-container">
              <h6 className="job-setup-section-title">Template Settings</h6>
              <div className="section-fields">
                <Form.Group>
                  <Form.Label>Section Template</Form.Label>
                  {/* No onLockClick: an inspection in progress has no unlock
                      path, so there is no hint dialog to open. */}
                  <LockedField
                    notice={sectionTemplateLocked ? "inspected" : null}
                  >
                    <Form.Select
                      value={
                        formData.template
                          ? formData.template.id
                          : sectionTemplates.find((t) => t.isSystemDefault)
                              ?.id || ""
                      }
                      onChange={(e) => {
                        const templateId = e.target.value;
                        const selectedTemplate = templateId
                          ? templateOptions.find((t) => t.id === templateId)
                          : null;
                        handleInputChange("template", selectedTemplate);
                      }}
                    >
                      {templateOptions.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </Form.Select>
                  </LockedField>
                </Form.Group>

                {contractTemplates.length > 0 && (
                  <Form.Group className="job-setup-contract-field">
                    <Form.Label>Contract</Form.Label>
                    <div className="job-setup-contract-row">
                      {/* Preview sits outside the lock: a sent contract is still
                          worth reading, it just can no longer be swapped. */}
                      <LockedField
                        notice={locks.contractTemplate}
                        onLockClick={openLockDialog}
                        showNotice={false}
                      >
                        <Form.Select
                          value={selectedContractTemplateId || ""}
                          onChange={(e) =>
                            setSelectedContractTemplateId(e.target.value)
                          }
                        >
                          {contractTemplates.map((tpl) => (
                            <option key={tpl.id} value={tpl.id}>
                              {tpl.name}
                            </option>
                          ))}
                        </Form.Select>
                      </LockedField>
                      {!contractSigned && (
                        <Button
                          variant="link"
                          onClick={() => setShowPreview(true)}
                          className="job-setup-preview-btn"
                        >
                          <Icon path={mdiTextBoxOutline} size="16px" />
                          Preview
                        </Button>
                      )}
                    </div>
                    <LockNotice
                      notice={locks.contractTemplate}
                      className="locked-field-notice--below"
                    />
                  </Form.Group>
                )}
              </div>
            </div>

            <div className="job-setup-section-container">
              <h6 className="job-setup-section-title">Quote Info</h6>
              <div className="section-fields">
                <Row>
                  <Col md={6}>
                    <LockedField
                      notice={locks.quote}
                      onLockClick={openLockDialog}
                    >
                      <Form.Group>
                        <Form.Label>Quote</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="Enter quote amount"
                          value={formData.quote}
                          onChange={(e) =>
                            handleInputChange("quote", e.target.value)
                          }
                          isInvalid={!!formErrors.quote}
                          min="0"
                          step="0.01"
                        />
                        <Form.Control.Feedback type="invalid">
                          {formErrors.quote}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </LockedField>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Add note..."
                        value={formData.note}
                        onChange={(e) =>
                          handleInputChange("note", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </div>
          </Form>
        </div>

        <div className="job-setup-footer">
          {contractTrack.show && contractTrack.alert && (
            <div className="job-setup-send-alert" role="alert">
              {contractTrack.alert}
            </div>
          )}
          {wantContract && contractTrack.warning && (
            <div className="job-setup-send-warning" role="status">
              {contractTrack.warning}
            </div>
          )}
          {trackStatesFailed && (
            <div className="job-setup-send-error" role="alert">
              <span>Couldn't load send options.</span>
              {onRetryTrackStates && (
                <Button
                  variant="link"
                  className="job-setup-send-retry"
                  onClick={onRetryTrackStates}
                >
                  Retry
                </Button>
              )}
            </div>
          )}
          <JobSetupSendTracks
            contract={{
              show: contractTrack.show,
              checked: contractChecked,
              disabled: contractTrack.disabled,
              tooltip: contractTrack.tooltip,
              onToggle: handleToggleContract,
              label: "Contract",
            }}
            payment={{
              show: paymentTrack.show,
              checked: paymentChecked,
              disabled: paymentTrack.disabled,
              tooltip: paymentTrack.tooltip,
              onToggle: handleTogglePayment,
              label: "Payment Request",
            }}
          />
          <div className="job-setup-footer-actions">
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading.jobSetup}
            >
              {primaryLabel}
            </Button>
          </div>
        </div>
      </div>

      <ContractPreviewLightbox
        show={showPreview}
        onHide={() => setShowPreview(false)}
        template={selectedContractTemplate}
        data={contractPreviewData}
      />

      <LockHintDialog
        show={lockDialog.show}
        variant={lockDialog.variant}
        onClose={() => setLockDialog((prev) => ({ ...prev, show: false }))}
        onGoToDetail={onNavigateToDetail}
      />

      <CustomToast
        messages={toasts}
        onClose={dismissToast}
        isMobile={device.isMobile}
        delay={4000}
        autohide={true}
      />
    </Modal>
  );
};

export default JobSetupModal;
